import * as Sentry from "@sentry/nextjs";
import { AxiosResponse } from "axios";
import { toast } from "react-toastify";
import { useSWRConfig } from "swr";

import { useErrorToaster } from "src/common/components/ErrorDetails";
import { client, endpoint, handleAllPromises } from "src/lib/bridgeClient";
import { SERVER_COMM_ERROR } from "src/lib/error";

export type CreateTemplateGroupDTO = {
  workflow_template_id: number;
  group_name: string;
};

export const createTemplateGroup = async (client: Client, group: CreateTemplateGroupDTO) => {
  return await client(endpoint.myOrgWorkflowTemplateGroups, "POST", group);
};

export const useCreateTemplateGroup = () => {
  const toastError = useErrorToaster();
  const { mutate } = useSWRConfig();

  return async (groups: CreateTemplateGroupDTO[]) => {
    if (!groups) {
      return;
    }
    let responses: PromiseSettledResult<AxiosResponse>[];

    try {
      const promises = [];

      for (const group of groups) {
        promises.push(createTemplateGroup(client, group));
      }

      // Make all requests async and wait for all them to resolve
      responses = await Promise.allSettled(promises);
      const { failures } = handleAllPromises(responses);
      const apiErrors: string[] = [];
      for (const failure of failures) {
        if (failure.response.data?.error) {
          apiErrors.push(failure.response.data?.error);
        }
      }

      if (apiErrors.length > 0) {
        toastError("Failed to add groups to workflow templates", apiErrors);
      }
      const success = apiErrors?.length === 0;
      mutate(endpoint.myOrgWorkflowTemplateGroups);
      return {
        data: responses,
        ok: success,
      };
    } catch (error) {
      toast.error(`Request failed: ${SERVER_COMM_ERROR}`);
      Sentry.captureException(error);

      return error.response;
    }
  };
};
